import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useHeroImage } from '../hooks/usePageHero';
import './first-briefcase.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function FirstBriefcasePage() {
  const [email, setEmail] = useState('');
  const heroImage = useHeroImage('first-briefcase');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/newsletter/subscribe`, { email: email.trim() });
      setDone(true);
      toast.success("You're in. Welcome to First Briefcase.");
    } catch (err) {
      const detail = err?.response?.data?.detail || '';
      if (detail.toLowerCase().includes('already')) {
        setDone(true);
        toast.info("You're already subscribed.");
      } else {
        toast.error('Could not sign up. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ background: '#F5F0E8', minHeight: '100vh' }}>
    <div className="fb-shell" data-testid="first-briefcase-page" style={{ width: '100%', margin: '0', background: '#F5F0E8', border: 'none', borderRadius: '0', boxShadow: 'none', overflow: 'hidden' }}>

      {/* ── HERO ── */}
      <section className="hero" style={heroImage ? { backgroundImage: `url(${heroImage})`, backgroundSize: 'cover', backgroundPosition: 'center' } : {}}>
        <div className="wrap hero-grid">
          <div className="hero-left">
            <div className="op-presents">
              <span className="op-dot" />
              Opportunity Post Presents
            </div>
            <div className="hero-wordmark">
              <span className="first" style={{ fontFamily: "'Inter Tight', sans-serif", fontWeight: 900, letterSpacing: '-0.05em', fontSize: 'clamp(2.5rem, 1.5rem + 4vw, 5rem)', color: '#0D2137', display: 'block', lineHeight: .88 }}>FIRST</span>
              <span className="briefcase" style={{ fontFamily: "'Inter Tight', sans-serif", fontWeight: 900, letterSpacing: '-0.05em', fontSize: 'clamp(2.2rem, 1.2rem + 3.5vw, 4.5rem)', color: '#1B6B72', display: 'block', lineHeight: .88 }}>BRIEFCASE</span>
            </div>
            <div className="hero-tagline">The Early-Career Opportunity Platform.</div>
          </div>
          <div className="hero-right">
            <div className="hero-value">
              Jobs. Internships.<br />Pathways. <em>Resources.</em>
            </div>
            <p className="hero-body">
              First Briefcase connects students and early-career talent to internships, campus jobs,
              career pathways, and partner-powered resources — all in one place,{' '}
              <strong>free for Opportunity Post members.</strong>
            </p>
            <div className="hero-cta">
              <Link to="/career-assessment" className="btn btn-primary" data-testid="fb-cta-primary">
                Join First Briefcase Free →
              </Link>
              <Link to="/career-hub" className="btn btn-ghost" data-testid="fb-cta-career-hub">
                Browse Career Hub
              </Link>
            </div>
            <div className="hero-bullets">
              <div className="hero-bullet">Free for all Opportunity Post members</div>
              <div className="hero-bullet">Real opportunities. Real partners.</div>
              <div className="hero-bullet">No spam. Cancel anytime.</div>
            </div>
          </div>
        </div>
      </section>

      {/* ── STUDENT SIGNAL STRIP ── */}
      <div className="ss-strip" data-testid="fb-ss-strip">
        <div className="wrap ss-inner">
          <div className="ss-left">
            <div className="ss-label">In Partnership With</div>
            <div className="ss-divider" />
            <div className="ss-name">StudentSignal</div>
            <div className="ss-divider" />
            <div className="ss-desc">
              College exploration, scholarships, and education pathways — bridging students to what comes next.
            </div>
          </div>
          <a
            href="https://studentsignal.com"
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-outline btn-sm"
            data-testid="fb-ss-strip-link"
          >
            Explore StudentSignal →
          </a>
        </div>
      </div>

      {/* ── THREE PILLARS ── */}
      <section className="pillars-section" data-testid="fb-pillars">
        <div className="wrap">
          <div className="kicker">What First Briefcase Delivers</div>
          <h2 style={{ fontFamily: 'var(--fb-serif)', fontSize: 'clamp(1.6rem,1.4rem + .5vw,2.2rem)', color: 'var(--fb-navy)' }}>
            Three pillars. One early-career platform.
          </h2>
          <div className="pillars-grid">
            <div className="pillar-card" data-testid="fb-pillar-01">
              <div className="pillar-num">01</div>
              <div className="pillar-icon p1">
                <svg width="20" height="20" fill="none" stroke="var(--fb-teal)" strokeWidth="1.8" viewBox="0 0 24 24">
                  <rect x="2" y="7" width="20" height="14" rx="2" />
                  <path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2" />
                </svg>
              </div>
              <h3>Jobs &amp; Internships</h3>
              <p>Curated student jobs, campus roles, internships, and early-career positions — across all sectors, updated continuously.</p>
            </div>
            <div className="pillar-card" data-testid="fb-pillar-02">
              <div className="pillar-num">02</div>
              <div className="pillar-icon p2">
                <svg width="20" height="20" fill="none" stroke="var(--fb-gold)" strokeWidth="1.8" viewBox="0 0 24 24">
                  <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
                </svg>
              </div>
              <h3>Career Pathways</h3>
              <p>Structured career paths, employer programs, and mentorship opportunities that build real skills and open real doors.</p>
            </div>
            <div className="pillar-card" data-testid="fb-pillar-03">
              <div className="pillar-num">03</div>
              <div className="pillar-icon p3">
                <svg width="20" height="20" fill="none" stroke="var(--fb-navy)" strokeWidth="1.8" viewBox="0 0 24 24">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                  <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                </svg>
              </div>
              <h3>Partner Resources</h3>
              <p>College planning, scholarships, certifications, and training programs — sourced through our mission-aligned partner network.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── ASSESSMENT CARD ── */}
      <section className="split-signal" data-testid="fb-assessment">
        <div className="wrap">
          <div className="assess-feature-card fb-assess-grid">
            {/* Left: description + steps */}
            <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
              <div>
                <div className="assess-feature-label">Free Career Assessment</div>
                <div className="assess-feature-name" style={{ fontSize: 'clamp(1.8rem,1.5rem + 1vw,3rem)', marginBottom: '.85rem', lineHeight: 1 }}>
                  Let us help you<br /><span style={{ fontStyle: 'normal', color: 'white' }}>find your</span> path.
                </div>
                <p style={{ fontSize: 'var(--fb-sp4)', color: 'rgba(255,255,255,.7)', lineHeight: 1.75, marginBottom: '1.4rem', maxWidth: '52ch' }}>
                  We're here to help you on this journey. Take the First Briefcase Career Assessment — just 4 quick questions that guide you to the right opportunities, resources, and pathways built for exactly where you are right now.
                </p>
                <div className="assess-steps" style={{ gap: '.6rem', marginBottom: '1.6rem' }}>
                  {[
                    'Tell us where you are right now',
                    'Share your sector interest and goals',
                    "Tell us if you're open to school",
                    'Get your curated match instantly',
                  ].map((step, i) => (
                    <div key={i} className="assess-step" style={{ padding: '.7rem .9rem', background: 'rgba(255,255,255,.08)', borderRadius: 'var(--fb-r-md)' }}>
                      <span className="assess-step-num">{i + 1}</span>
                      {step}
                    </div>
                  ))}
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1.25rem', flexWrap: 'wrap' }}>
                <Link to="/career-assessment" className="btn btn-gold" data-testid="career-assessment-cta">
                  Take the Assessment →
                </Link>
                <span style={{ fontSize: 'var(--fb-sp4)', fontSize: '.72rem', fontWeight: 700, color: 'rgba(255,255,255,.45)' }}>
                  Free forever · No experience required · 4 minutes
                </span>
              </div>
            </div>
            {/* Right: stats panel */}
            <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', textAlign: 'center', background: 'rgba(255,255,255,.06)', borderRadius: 'var(--fb-r-xl)', padding: '1rem' }}>
              <div style={{ fontFamily: 'var(--fb-serif)', fontSize: '6rem', color: 'var(--fb-gold)', lineHeight: 1, fontWeight: 400 }}>4</div>
              <div style={{ fontSize: '.68rem', fontWeight: 900, textTransform: 'uppercase', letterSpacing: '.1em', color: 'rgba(255,255,255,.35)', marginTop: '.35rem', marginBottom: '1.75rem' }}>
                Quick Questions
              </div>
              <div style={{ display: 'grid', gap: '.85rem', width: '100%' }}>
                {[
                  { label: 'Takes only', value: '4 minutes' },
                  { label: 'Cost', value: 'Always free' },
                  { label: 'Results', value: 'Instant match' },
                ].map(({ label, value }) => (
                  <div key={label} style={{ background: 'rgba(255,255,255,.08)', borderRadius: 'var(--fb-r-md)', padding: '.85rem', textAlign: 'left' }}>
                    <div style={{ fontSize: '.68rem', fontWeight: 900, textTransform: 'uppercase', letterSpacing: '.08em', color: 'rgba(255,255,255,.35)', marginBottom: '.25rem' }}>{label}</div>
                    <div style={{ fontSize: '1.1rem', fontWeight: 900, color: 'white' }}>{value}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── BUILT FOR ── */}
      <section className="for-section" data-testid="fb-audiences">
        <div className="wrap">
          <div className="kicker">Built For</div>
          <h2 style={{ fontFamily: 'var(--fb-serif)', fontSize: 'clamp(1.6rem,1.4rem + .5vw,2.2rem)', color: 'var(--fb-navy)' }}>
            If you're starting out, this is for you.
          </h2>
          <div className="for-grid">
            <div className="for-card" data-testid="fb-audience-students">
              <div className="for-dot" />
              <h3>Students &amp; Interns</h3>
              <p>Part-time jobs, on-campus roles, and internships that fit your schedule and build your resume. No experience required to start.</p>
            </div>
            <div className="for-card" data-testid="fb-audience-starters">
              <div className="for-dot" style={{ background: 'var(--fb-gold)' }} />
              <h3>Career Starters</h3>
              <p>Entry-level roles, training pathways, and first-move resources for grads launching their professional journey — at any age.</p>
            </div>
            <div className="for-card" data-testid="fb-audience-movers">
              <div className="for-dot" style={{ background: 'var(--fb-navy)' }} />
              <h3>Early Movers</h3>
              <p>Opportunity-focused resources and partner pathways for students and recent grads ready to move fast into the workforce.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS ── */}
      <section className="how-section" id="how" data-testid="fb-how-it-works">
        <div className="wrap">
          <div className="sc" style={{ padding: '1rem' }}>
            <div className="kicker">How It Works</div>
            <h2 style={{ fontFamily: 'var(--fb-serif)', fontSize: 'clamp(1.5rem,1.3rem + .45vw,2rem)', color: 'var(--fb-navy)', marginBottom: '1rem' }}>
              Four steps. Zero friction.
            </h2>
            <div className="how-grid">
              {[
                { num: '01', title: 'Join Free', desc: 'Create your free Opportunity Post account and unlock First Briefcase access in seconds. No credit card. No commitment.' },
                { num: '02', title: 'Share Your Goals', desc: 'Tell us your interests, skills, and career direction so we can surface what matters to you — and only what matters to you.' },
                { num: '03', title: 'Discover Opportunities', desc: 'Browse jobs, internships, college pathways, and partner resources — curated to your profile and updated every week.' },
                { num: '04', title: 'Move Toward Outcomes', desc: 'Take action, build experience, and reach your career and education goals with real support from real partners.' },
              ].map(({ num, title, desc }) => (
                <div key={num} className="how-card" data-testid={`fb-step-${num}`}>
                  <div className="how-num">{num}</div>
                  <div className="how-text">
                    <h4>{title}</h4>
                    <p>{desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── THIN CTA STRIP — email signup ── */}
      <section style={{ padding: '24px 0' }} data-testid="fb-signup-section" id="fb-signup">
        <div className="wrap">
          <div style={{ background: 'linear-gradient(135deg,#0D2137,#102B45)', borderRadius: 'var(--fb-r-xl)', padding: '1rem 1.5rem', display: 'grid', gridTemplateColumns: '1fr 1fr', alignItems: 'center', gap: '2rem', boxShadow: 'var(--fb-sh-md)' }}>
            {/* Left */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '1.1rem' }}>
              <div style={{ width: 52, height: 52, borderRadius: 12, background: 'var(--fb-teal)', display: 'grid', placeItems: 'center', flexShrink: 0, boxShadow: '0 6px 18px rgba(27,107,114,.35)' }}>
                <svg width="26" height="26" fill="none" viewBox="0 0 24 24" stroke="white" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="7" width="20" height="14" rx="2" />
                  <path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2" />
                  <path d="M8 14l2.5 2.5L16 11" />
                </svg>
              </div>
              <div>
                <div style={{ fontSize: '.85rem', fontWeight: 900, textTransform: 'uppercase', letterSpacing: '.1em', color: 'var(--fb-gold)', marginBottom: '.35rem' }}>First Briefcase</div>
                <div style={{ fontFamily: 'var(--fb-serif)', fontSize: 'clamp(1.4rem,1.2rem + .6vw,2rem)', color: 'white', lineHeight: 1, marginBottom: '.4rem' }}>
                  <em style={{ fontStyle: 'italic', color: 'var(--fb-gold)' }}>Opportunity</em> starts here.
                </div>
                <div style={{ fontSize: '.9rem', color: 'rgba(255,255,255,.55)', fontWeight: 600, lineHeight: 1.6, maxWidth: '38ch' }}>
                  Unlock jobs, internships, pathways, and training — updated every week.
                </div>
              </div>
            </div>
            {/* Right — email form */}
            <div>
              {done ? (
                <div style={{ background: 'rgba(27,107,114,.25)', border: '1.5px solid rgba(27,107,114,.5)', borderRadius: 10, padding: '14px 20px', color: 'white', fontWeight: 600, fontSize: '.9rem' }} data-testid="fb-signup-success">
                  You're in. Welcome to First Briefcase.
                </div>
              ) : (
                <form onSubmit={handleSubmit} data-testid="fb-signup-form">
                  <div style={{ display: 'flex', alignItems: 'center', gap: '.5rem', background: 'rgba(255,255,255,.09)', border: '1px solid rgba(255,255,255,.1)', borderRadius: 10, padding: '6px', marginBottom: '.55rem' }}>
                    <svg width="16" height="16" fill="none" stroke="rgba(255,255,255,.4)" strokeWidth="2" viewBox="0 0 24 24" style={{ flexShrink: 0, marginLeft: '.5rem' }}>
                      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                      <polyline points="22,6 12,13 2,6" />
                    </svg>
                    <input
                      type="email"
                      required
                      placeholder="Enter your email address"
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                      style={{ flex: 1, border: 0, background: 'transparent', padding: '0 .75rem', color: 'white', minHeight: 42, outline: 0, fontSize: '.88rem', fontFamily: 'var(--fb-sans)' }}
                      data-testid="fb-email-input"
                    />
                    <button
                      type="submit"
                      disabled={submitting}
                      style={{ background: 'var(--fb-gold)', color: 'var(--fb-navy)', borderRadius: 8, padding: '.65rem 1.25rem', fontWeight: 900, fontSize: '.85rem', whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: '.4rem', border: 'none', cursor: submitting ? 'wait' : 'pointer', opacity: submitting ? 0.7 : 1, fontFamily: 'var(--fb-sans)' }}
                      data-testid="fb-signup-submit"
                    >
                      <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                        <path d="M5 12h14M12 5l7 7-7 7" />
                      </svg>
                      {submitting ? 'Joining…' : 'Join Now'}
                    </button>
                  </div>
                  <div style={{ display: 'flex', gap: '1.25rem' }}>
                    {['No spam', 'Cancel anytime', 'Free forever'].map(label => (
                      <span key={label} style={{ display: 'flex', alignItems: 'center', gap: '.4rem', fontSize: '.72rem', fontWeight: 800, color: 'rgba(255,255,255,.4)' }}>
                        <span style={{ width: 5, height: 5, borderRadius: '50%', background: '#33c9ac', display: 'inline-block' }} />
                        {label}
                      </span>
                    ))}
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ── PARTNERSHIPS ── */}
      <section className="partner-section" id="partners" data-testid="fb-partnerships">
        <div className="wrap">
          <div className="kicker">Powered by Partnerships</div>
          <h2 style={{ fontFamily: 'var(--fb-serif)', fontSize: 'clamp(1.6rem,1.4rem + .5vw,2.2rem)', color: 'var(--fb-navy)', marginBottom: '1rem' }}>
            Built through a network of employers, colleges, and mission-aligned partners.
          </h2>
          <div className="partner-top-grid">
            <div className="partner-card">
              <div className="partner-dot" />
              <h4>Employer Partners</h4>
              <p>Companies offering internships, mentorship, early hiring pipelines, and career development pathways for first-time talent.</p>
            </div>
            <div className="partner-card">
              <div className="partner-dot" style={{ background: 'var(--fb-teal)' }} />
              <h4>College &amp; Training Partners</h4>
              <p>Degree pathways, admissions support, certifications, and skills programs to build credentials that matter to employers.</p>
            </div>
            <div className="partner-card">
              <div className="partner-dot" style={{ background: 'var(--fb-navy)' }} />
              <h4>Member Resource Support</h4>
              <p>Personalized tools, guides, and one-on-one guidance from our partner success and member support teams.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── FEATURED RESOURCES ── */}
      <section className="resource-section" id="resources" data-testid="fb-resources">
        <div className="wrap">
          <div className="resource-header">
            <div>
              <div className="kicker">Featured Resources</div>
              <h2 style={{ fontFamily: 'var(--fb-serif)', fontSize: 'clamp(1.5rem,1.3rem + .45vw,2rem)', color: 'var(--fb-navy)' }}>
                Guides and tools to move with confidence.
              </h2>
            </div>
            <Link to="/career-hub" className="btn btn-outline btn-sm" data-testid="fb-resources-browse">
              Browse Career Hub →
            </Link>
          </div>
          <div className="resource-grid">
            <div className="resource-card">
              <span className="resource-type guide">Guide</span>
              <h4>Internship Success Guide</h4>
              <p>How to find, apply for, and succeed in internships that open real career doors. Step-by-step for first-timers.</p>
              <button
                onClick={() => document.getElementById('fb-signup')?.scrollIntoView({ behavior: 'smooth', block: 'center' })}
                className="resource-link"
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                data-testid="fb-resource-guide"
              >
                Access Resource →
              </button>
            </div>
            <div className="resource-card">
              <span className="resource-type brief">Opportunity Brief</span>
              <h4>Student &amp; University Brief</h4>
              <p>Hiring trends, in-demand roles, and the sectors actively recruiting student talent right now.</p>
              <button
                onClick={() => document.getElementById('fb-signup')?.scrollIntoView({ behavior: 'smooth', block: 'center' })}
                className="resource-link"
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                data-testid="fb-resource-brief"
              >
                Access Resource →
              </button>
            </div>
            <div className="resource-card">
              <span className="resource-type resource">Resource</span>
              <h4>Career Planning Toolkit</h4>
              <p>Worksheets, frameworks, and practical tools to build a career plan that actually works — even if you're starting from zero.</p>
              <button
                onClick={() => document.getElementById('fb-signup')?.scrollIntoView({ behavior: 'smooth', block: 'center' })}
                className="resource-link"
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                data-testid="fb-resource-toolkit"
              >
                Access Resource →
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ── STUDENT SIGNAL FEATURE CARD ── */}
      <section style={{ padding: '24px 0' }} data-testid="fb-ss-card">
        <div className="wrap">
          <div className="ss-feature-card fb-ss-card-grid" id="student-signal" style={{ background: 'linear-gradient(135deg, #1a4d2e, #0f3320)' }}>
            <div>
              <div className="ss-feature-label">In Partnership With</div>
              <div className="ss-feature-name" style={{ fontSize: '2.2rem', marginBottom: '.65rem' }}>StudentSignal</div>
              <p style={{ fontSize: '.88rem', color: 'white', lineHeight: 1.72, marginBottom: '.6rem', maxWidth: '62ch' }}>
                StudentSignal helps students explore colleges, scholarships, and next-step education pathways — while First Briefcase connects them to jobs, internships, and early-career opportunities.
              </p>
              <em className="ss-feature-em">
                Together, we cover the full picture: where you're going and how you'll get there.
              </em>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '.5rem .85rem' }}>
                {[
                  'College search and comparison tools',
                  'Scholarship discovery and tracking',
                  'Education pathway planning',
                  'Admissions guidance and support',
                ].map(item => (
                  <div key={item} className="ss-highlight">{item}</div>
                ))}
              </div>
            </div>
            <div style={{ textAlign: 'center', minWidth: 160 }}>
              <a
                href="https://studentsignal.com"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-ss"
                style={{ display: 'inline-flex', marginBottom: '.85rem' }}
                data-testid="fb-ss-card-link"
              >
                Explore StudentSignal →
              </a>
              <div style={{ fontSize: '.72rem', color: 'rgba(255,255,255,.7)', fontWeight: 700 }}>
                A First Briefcase<br />partner platform
              </div>
            </div>
          </div>
        </div>
      </section>

    </div>
    </div>
  );
}
